import React, { useState } from 'react';
import { Container, Typography, Box, Paper, Button, LinearProgress, Alert, Chip, Grid, Card, CardContent } from '@mui/material';
import { CloudUpload, CheckCircle, Error, Warning } from '@mui/icons-material';
import { CSVProcessor } from '../../utils/csvProcessor';
import apiClient from '../../utils/apiClient';
import { toast } from 'react-toastify';
import VerificationResults from './VerificationResults';

const CSVVerifier = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [results, setResults] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setResults(null);
    }
  };

  const handleVerify = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    setVerifying(true);
    console.log('🔍 Starting verification for:', selectedFile.name);

    try {
      // Process CSV with date conversion
      const batches = await CSVProcessor.splitCSV(selectedFile, 10000, 1);
      const processedFile = batches[0].data;

      console.log('📤 Sending verification request to backend');
      const response = await apiClient.uploadBatch(processedFile, '/api/v1/verifyMigratedTransactions/');
      
      console.log('✅ Verification complete:', response);
      console.log('💰 Amount comparison:', response.amount_comparison);
      setResults(response);
      toast.success('Verification completed successfully!');
    } catch (error) {
      console.error('❌ Verification failed:', error);
      toast.error(`Verification failed: ${error.message}`);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box py={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          CSV Transaction Verification Tool
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          Upload a CSV file to verify if transactions exist in the database
        </Typography>

        <Paper elevation={2} sx={{ p: 3, mb: 3, mt: 3 }}>
          <Box textAlign="center">
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-verifier-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="csv-verifier-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                size="large"
              >
                Select CSV File
              </Button>
            </label>

            {selectedFile && (
              <Box mt={2}>
                <Chip 
                  label={`Selected: ${selectedFile.name}`} 
                  color="primary" 
                  onDelete={() => setSelectedFile(null)}
                />
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleVerify}
                    disabled={verifying}
                    size="large"
                  >
                    {verifying ? 'Verifying...' : 'Verify Transactions'}
                  </Button>
                </Box>
              </Box>
            )}

            {verifying && (
              <Box mt={3}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Verifying transactions against database...
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {results && (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Total Rows
                        </Typography>
                        <Typography variant="h4">
                          {results.summary?.total_rows_in_csv || 0}
                        </Typography>
                      </Box>
                      <CheckCircle sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Found in DB
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {results.summary?.found_in_db || 0}
                        </Typography>
                      </Box>
                      <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Amount Mismatches
                        </Typography>
                        <Typography variant="h4" color="warning.main">
                          {results.summary?.amount_mismatches || 0}
                        </Typography>
                      </Box>
                      <Warning sx={{ fontSize: 40, color: 'warning.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Missing
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {results.summary?.missing_from_db || 0}
                        </Typography>
                      </Box>
                      <Warning sx={{ fontSize: 40, color: 'error.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={2.4}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography color="textSecondary" gutterBottom>
                          Errors
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {results.summary?.errors || 0}
                        </Typography>
                      </Box>
                      <Error sx={{ fontSize: 40, color: 'error.main' }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6">
                Accuracy: {results.summary?.accuracy_percentage?.toFixed(2)}%
              </Typography>
              <Typography variant="body2">
                CSV File: {results.csv_filename}
              </Typography>
              {results.amount_comparison && (
                <Box mt={1}>
                  <Typography variant="body2">
                    CSV Total: ₹{results.amount_comparison.csv_total_amount?.toFixed(2)} | 
                    DB Total: ₹{results.amount_comparison.db_total_amount?.toFixed(2)} | 
                    Missing Total: ₹{results.amount_comparison.missing_total?.toFixed(2)} | 
                    Amount Mismatch Total: ₹{results.amount_comparison.amount_mismatch_total?.toFixed(2)} | 
                    Extra in DB: ₹{results.amount_comparison.extra_in_db_total?.toFixed(2)} | 
                    Difference: ₹{results.amount_comparison.difference?.toFixed(2)}
                    {results.amount_comparison.match ? ' ✅ Match' : ' ❌ Mismatch'}
                  </Typography>
                </Box>
              )}
            </Alert>

            <VerificationResults results={results} />
          </>
        )}
      </Box>
    </Container>
  );
};

export default CSVVerifier;
